import { EagerLoadTreeDataSupplier } from 'business/tree/TreeDataSupplier';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import Explorer, { TreeControlState } from 'components/explorer';
import { InputWithIcon } from 'components/input';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  collectDescendantNodeIds,
  collectIndeterminateNodeIds,
  collectSearchMatchAncestorIds,
  expandNodesByIds,
} from './utils';

type ArchiveExplorerProps = {
  treeControlState: TreeControlState;
  setTreeControlState: React.Dispatch<React.SetStateAction<TreeControlState>>;
  disabled?: boolean;
};

export const ArchiveExplorer = (props: ArchiveExplorerProps) => {
  const { treeControlState, setTreeControlState, disabled = false } = props;
  const explorerRef = useRef(null);
  const [searchText, setSearchText] = useState('');

  const filteredTree = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    const filterNodes = (node) => {
      let filteredChildren = undefined;

      if (node.children?.length) {
        filteredChildren = node.children.map(filterNodes).filter(Boolean);
      }

      const matches =
        !text || node.name.toLowerCase().includes(text) || filteredChildren?.length > 0;

      if (!matches) {
        return null;
      }

      return {
        ...node,
        children: filteredChildren,
      };
    };

    return filterNodes;
  }, [searchText]);

  // Expand the branches leading to search matches once per search-term change.
  // Doing this here (instead of forcing expansion inside `filterNodes` on every
  // render) lets the user manually collapse a branch afterwards without it snapping
  // back open while the search text stays the same.
  useEffect(() => {
    setTreeControlState((prev) => {
      const idsToExpand = collectSearchMatchAncestorIds(prev.treenodes, searchText);
      if (!idsToExpand.size) {
        return prev;
      }
      const treenodes = expandNodesByIds(prev.treenodes, idsToExpand);
      return treenodes === prev.treenodes ? prev : { ...prev, treenodes };
    });
  }, [searchText, setTreeControlState]);

  const explorerTreeControlState = useMemo(
    () => ({
      ...treeControlState,
      indeterminateExplorerNodes: collectIndeterminateNodeIds(
        treeControlState.treenodes,
        treeControlState.checkedExplorerNodes,
      ),
    }),
    [treeControlState, treeControlState.treenodes, treeControlState.checkedExplorerNodes],
  );

  const onSelectExplorerNode = (nodeId) => {
    if (disabled) {
      return;
    }
    setTreeControlState((prev) => {
      const isSelected = prev.checkedExplorerNodes.includes(nodeId);
      const descendants = collectDescendantNodeIds(prev.treenodes, nodeId);
      let checkedExplorerNodes;
      let treenodes = prev.treenodes;
      if (isSelected) {
        // remove nodeId and any descendants
        const toRemove = new Set(descendants.concat([nodeId]));
        checkedExplorerNodes = prev.checkedExplorerNodes.filter(
          (id) => !toRemove.has(id),
        );
      } else {
        // add nodeId and all descendants (avoid duplicates)
        const toAdd = (descendants.length ? descendants : [nodeId]).concat([nodeId]);
        const set = new Set(prev.checkedExplorerNodes);
        toAdd.forEach((id) => set.add(id));
        checkedExplorerNodes = Array.from(set);
        // auto-expand the selected node and its entire sub-tree
        treenodes = expandNodesByIds(prev.treenodes, new Set(toAdd));
      }
      return {
        ...prev,
        treenodes,
        checkedExplorerNodes,
      };
    });
  };

  return (
    <>
      <div className={`basis-[10%] ${disabled ? 'pointer-events-none opacity-50' : ''}`}>
        <InputWithIcon
          key={'inputIcon'}
          icon={<RMSvgIcon name="Search" height="1.25rem" width="1.25rem" />}
          onChange={(e) => {
            if (disabled) {
              return;
            }
            setSearchText(e.target.value);
          }}
          value={searchText}
          placeholder="Search"
          inputStyle={{ height: '2.5rem' }}
          iconPosition="left"
        />
      </div>
      <div className="basis-[90%] border-solid border border-[#E0E0E0] rounded-sm p-2">
        <Explorer
          ref={explorerRef}
          backgroundColor="white"
          setTreeControlState={setTreeControlState}
          dataSupplier={EagerLoadTreeDataSupplier}
          treeControlState={explorerTreeControlState}
          shouldRenderNode={filteredTree}
          allowedRootTreeNodeTypes={[
            ETreeNodeTypes.Project,
            ETreeNodeTypes.ProjectFolder,
            ETreeNodeTypes.Projects,
            ETreeNodeTypes.ProjectsFolder,
          ]}
          shouldListenToBroadcastedEvents={false}
          showQuickAccessNode={false}
          showRecycleBinNode={false}
          isSearchEnabled={true}
          onCheckboxChange={onSelectExplorerNode}
          checkboxDisabled={disabled}
        />
      </div>
    </>
  );
};
